import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { NotifierType, showNotification } from '@core/components';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import useMaterials from '@ee-api/materials-api/useMaterials';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { MaterialAndColor, updateRequestedColor } from '@redux/slices/materialSlice';

import { getColorRoomvoSku } from '../../../pages/easy-estimator/components/scrollable-area/colors/colors.utils';

export interface RoomvoPostEvent {
    action: string;
    customData: string;
    eventExtra: unknown;
    eventLabel: string;
    iframeId: string;
    integratorVendorUrlPath: string;
}

export interface RoomvoCustomData {
    sku?: string;
}

interface RoomvoResponseProps {
    onAddToCart?: (sku: string) => void;
}

interface RoomvoResponse {
    lastAction?: string;
    visitorId?: string;
    selectedRoom?: string;
    selectedSku?: string;
    viewedSkus?: string[];
    viewedRooms?: string[];
}

export type SelectedItemInfo = { sku: string; count: number };
export type SelectedItemMap = { [id: string]: SelectedItemInfo };

export type CustomDataDownload = { image: string };
export type CustomDataAddToCart = { sku: string };

export interface FFTrack {
    action: string;
    eventAction: string;
    eventLabel: string;
}

export interface AddToCart extends FFTrack {
    customData?: string | CustomDataAddToCart; // this is json
}

export interface DownloadImage extends FFTrack {
    customData?: string | CustomDataDownload; // this is json
}

export interface VisitorId extends FFTrack {
    visitorId: string;
}

export type RoomvoResponseData = FFTrack | VisitorId | AddToCart;

export const useRoomvoEventListener = (props?: RoomvoResponseProps): RoomvoResponse => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { materials } = useMaterials();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { selectedValue: selectedColor } = useSelectedColor();

    const [visitorId, setVisitorId] = useState<string | undefined>(undefined);
    const [lastAction, setLastAction] = useState<string | undefined>(undefined);
    const [selectedRoom, setSelectedRoom] = useState<string | undefined>();
    const [selectedSku, setSelectedSku] = useState<string | undefined>();
    const [viewedSkus, setViewedSkus] = useState<string[]>([]);
    const [viewedRooms, setViewedRooms] = useState<string[]>([]);
    const [startTime, setStartTime] = useState(Date.now());

    const { trackVisualizer } = useAnalyticsEvents();

    const trackRoomvoAction = (action: string, label?: string, value?: number) => {
        // console.log('==trackRoomvoAction', action, label, value);
        trackVisualizer({
            action: action,
            payload: {
                ...(label ? { label: label } : {}), // optional
                ...(value ? { value: value } : {}), // optional, must be a number
            },
        });
    };

    const updateSelectedRoom = (room?: string) => {
        if (!room) {
            return;
        }
        setSelectedRoom(room);
        setViewedRooms([...viewedRooms, room]);
    };

    const updateSelectedSku = (sku?: string) => {
        if (!sku) {
            return;
        }
        setSelectedSku(sku);
        setViewedSkus([...viewedSkus, sku]);
    };

    const handleFfTrack = (data: RoomvoResponseData) => {
        if (data.action.match('ffTrack')) {
            const room = data.eventAction.match('Selected Room') ? data.eventLabel : undefined;
            const sku = data.eventAction.match('product view') ? data.eventLabel : undefined;
            const share = data.eventAction.match('Share Room') ? data.eventLabel : undefined;
            const download = data.eventAction.match('Download image') ? data.eventLabel : undefined;

            trackRoomvoAction(data.eventAction, data.eventLabel);
            updateSelectedRoom(room);
            updateSelectedSku(sku);
            if (download) {
                console.log('==download', download);
            }
            if (share) {
                console.log('==share', share);
            }
        }
    };

    const handleFfSaveVisitor = (data: RoomvoResponseData) => {
        if (data.action.match('ffSaveVisitor')) {
            trackRoomvoAction('ffSaveVisitor', (data as VisitorId).visitorId);
            setVisitorId((data as VisitorId).visitorId);
        }
    };

    const handleAddToCart = (data: AddToCart) => {
        if (data.action.match('roomvoAddToCart') && data.customData !== undefined) {
            const customData = JSON.parse(data.customData as string);
            const sku = customData?.sku;
            trackRoomvoAction(data.action, sku);
            updateSelectedSku(sku);
            if (customData) {
                doChangeColor(customData);
            }
            if (props?.onAddToCart) {
                props.onAddToCart(sku);
            }
        }
    };

    const doChangeColor = (customData: RoomvoCustomData) => {
        const newMaterialAndColor = materials.reduce((materialAndColor: MaterialAndColor | undefined, material) => {
            if (materialAndColor) {
                return materialAndColor;
            }
            const color = material.colors.find((color) => getColorRoomvoSku(color) === customData.sku);
            const changeMaterial = selectedMaterial?.description !== material.description;
            return color
                ? {
                      color,
                      material: changeMaterial ? material : undefined,
                  }
                : undefined;
        }, undefined);

        // if neither the material or color changed, bail out
        if (
            newMaterialAndColor &&
            newMaterialAndColor.material?.description === selectedMaterial?.description &&
            newMaterialAndColor.color.productId === selectedColor?.productId
        ) {
            return;
        }

        if (newMaterialAndColor) {
            dispatch(updateRequestedColor(newMaterialAndColor));
        } else {
            showNotification({
                type: NotifierType.WARNING,
                title: t('EQUIVALENT_PRODUCT_MATCHING.ROOMVO.COLOR_NOT_AVAILABLE.TITLE'),
                description: t('EQUIVALENT_PRODUCT_MATCHING.ROOMVO.COLOR_NOT_AVAILABLE.DESCRIPTION', {
                    color: customData.sku,
                }),
                message: t('EQUIVALENT_PRODUCT_MATCHING.ROOMVO.COLOR_NOT_AVAILABLE.MESSAGE'),
                duration: 30,
            });
        }
    };

    const handleFocusPopup = (data: RoomvoResponseData) => {
        if (data.action.match('ffFocusPopup')) {
            trackRoomvoAction('Open Roomvo');
            setStartTime(Date.now());
        }
        if (data.action.match('roomvoCloseVisualizer') || data.action.match('roomvoAddToCart')) {
            const endTime = Date.now();
            const duration = (endTime - startTime) / 1000;
            trackRoomvoAction(`Exit ${data.action}`, undefined, duration);
        }
    };

    // const handleMessage = useCallback(
    //     (event: MessageEvent) => {
    //         if (!event.origin.match('www.roomvo.com')) {
    //             return;
    //         }
    //
    //         const roomvoData: RoomvoPostEvent = event.data;
    //         if (roomvoData.action !== 'roomvoAddToCart') {
    //             return;
    //         }
    //
    //         const customData: RoomvoCustomData = JSON.parse(roomvoData.customData);
    //         if (!customData.sku) {
    //             return;
    //         }
    //
    //         const newMaterialAndColor = materials.reduce((materialAndColor: MaterialAndColor | undefined, material) => {
    //             if (materialAndColor) {
    //                 return materialAndColor;
    //             }
    //             const color = material.colors.find((color) => getColorRoomvoSku(color) === customData.sku);
    //             const changeMaterial = selectedMaterial?.description !== material.description;
    //             return color
    //                 ? {
    //                       color,
    //                       material: changeMaterial ? material : undefined,
    //                   }
    //                 : undefined;
    //         }, undefined);
    //
    //         // if neither the material or color changed, bail out
    //         if (
    //             newMaterialAndColor &&
    //             newMaterialAndColor.material?.description === selectedMaterial?.description &&
    //             newMaterialAndColor.color.productId === selectedColor?.productId
    //         ) {
    //             return;
    //         }
    //
    //         if (newMaterialAndColor) {
    //             dispatch(updateRequestedColor(newMaterialAndColor));
    //         } else {
    //             showNotification(
    //                 NotifierType.WARNING,
    //                 t('EQUIVALENT_PRODUCT_MATCHING.ROOMVO.COLOR_NOT_AVAILABLE.TITLE'),
    //                 t('EQUIVALENT_PRODUCT_MATCHING.ROOMVO.COLOR_NOT_AVAILABLE.DESCRIPTION', { color: customData.sku }),
    //                 t('EQUIVALENT_PRODUCT_MATCHING.ROOMVO.COLOR_NOT_AVAILABLE.MESSAGE'),
    //                 30
    //             );
    //         }
    //     },
    //     [dispatch, materials, selectedColor?.productId, selectedMaterial?.description, t]
    // );

    const handleRoomvoMessage = useCallback(
        (event: MessageEvent<RoomvoResponseData>) => {
            if (event.origin.match('roomvo.com')) {
                setLastAction(event.data.action);
                handleFfTrack(event.data);
                handleFfSaveVisitor(event.data);
                handleAddToCart(event.data);
                handleFocusPopup(event.data);
                console.log('==event', event.data);
            }
        },
        [handleAddToCart, handleFfSaveVisitor, handleFfTrack, handleFocusPopup]
    );

    useEffect(() => {
        window.addEventListener('message', handleRoomvoMessage, false);

        return () => {
            window.removeEventListener('message', handleRoomvoMessage);
        };
    }, [handleRoomvoMessage]);

    return {
        lastAction,
        visitorId,
        selectedRoom,
        selectedSku,
        viewedRooms,
        viewedSkus,
    };
};
